import {
  Box,
  Button,
  Card,
  Checkbox,
  Field,
  Heading,
  Input,
  InputGroup,
  NativeSelect,
  Text,
} from "@chakra-ui/react";
import {
  INTEGRAL_COVERAGE_OPTIONS,
  integralCoverageValue,
  parseIntegralCoverage,
} from "../integralCoverage";
import { type BenchRow, isBenchValid, rowContracts, rowErrors } from "../bench";
import { INSURERS } from "../insurers";

type Props = {
  rows: BenchRow[];
  onAdd: () => void;
  onRemove: (id: number) => void;
  onEdit: (id: number, patch: Partial<Omit<BenchRow, "id">>) => void;
  onNext: () => void;
  portfolio?: number;
};

const BenchStep = ({
  rows,
  onAdd,
  onRemove,
  onEdit,
  onNext,
  portfolio,
}: Props): JSX.Element => (
  <Card.Root borderRadius="lg" border="none">
    <Card.Body p="5" display="flex" flexDirection="column" gap="4">
      <Box>
        <Heading size="xl">¿Qué fianzas o seguros tienes hoy?</Heading>
        <Text color="gray.600">
          Cuéntanos con qué aseguradoras y afianzadoras trabajas, cuántos
          contratos tienes con cada una y qué tasa pagas hoy. Con eso buscamos
          la mejor tasa para ti.
        </Text>
      </Box>
      {rows.map((row) => {
        const errors = rowErrors(row);
        return (
          <Box
            key={row.id}
            display="grid"
            gridTemplateColumns={{
              base: "1fr 1fr",
              md: "2fr 1fr 1fr 1fr 1fr auto",
            }}
            gap="3"
            alignItems="start"
            pb="4"
            borderBottom="1px solid #ebecef80"
          >
            <Field.Root gridColumn={{ base: "1 / -1", md: "auto" }}>
              <Field.Label>Afianzadora o aseguradora</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  value={row.insurer}
                  onChange={(e) => onEdit(row.id, { insurer: e.target.value })}
                >
                  {INSURERS.map((insurer) => (
                    <option key={insurer} value={insurer}>
                      {insurer}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
            <Field.Root invalid={!!errors.contractCount}>
              <Field.Label>
                {row.unit === "percent" ? "% de la cartera" : "# de contratos"}
              </Field.Label>
              <InputGroup endElement={row.unit === "percent" ? "%" : undefined}>
                <Input
                  inputMode="numeric"
                  value={row.contractCount}
                  onChange={(e) =>
                    onEdit(row.id, {
                      contractCount: e.target.value.replace(/\D/g, ""),
                    })
                  }
                />
              </InputGroup>
              <Field.ErrorText>{errors.contractCount}</Field.ErrorText>
              {row.unit === "percent" && !errors.contractCount && (
                <Field.HelperText>
                  ≈ {rowContracts(row, portfolio)} de {portfolio} contratos
                </Field.HelperText>
              )}
              {!!portfolio && (
                <Button
                  variant="plain"
                  size="xs"
                  p="0"
                  h="auto"
                  color="palomma.500"
                  onClick={() =>
                    onEdit(row.id, {
                      unit: row.unit === "percent" ? "count" : "percent",
                      contractCount: "",
                    })
                  }
                >
                  {row.unit === "percent"
                    ? "Ingresar # de contratos"
                    : "Ingresar % de la cartera"}
                </Button>
              )}
            </Field.Root>
            <Field.Root invalid={!!errors.rate}>
              <Field.Label>Precio (% del canon)</Field.Label>
              <InputGroup endElement="%">
                <Input
                  inputMode="decimal"
                  value={row.rate}
                  onChange={(e) =>
                    onEdit(row.id, {
                      rate: e.target.value
                        .replace(",", ".")
                        .replace(/[^\d.]/g, "")
                        .replace(/^(\d*\.\d*)\..*$/, "$1"),
                    })
                  }
                />
              </InputGroup>
              <Field.ErrorText>{errors.rate}</Field.ErrorText>
            </Field.Root>
            <Field.Root>
              <Field.Label visibility="hidden">IVA</Field.Label>
              <Box h="10" display="flex" alignItems="center">
                <Checkbox.Root
                  checked={row.includesVat}
                  onCheckedChange={(e) =>
                    onEdit(row.id, { includesVat: e.checked === true })
                  }
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>IVA incluido</Checkbox.Label>
                </Checkbox.Root>
              </Box>
            </Field.Root>
            <Field.Root>
              <Field.Label>Amparo integral</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  value={integralCoverageValue(row.integralCoverage)}
                  onChange={(e) =>
                    onEdit(row.id, {
                      integralCoverage: parseIntegralCoverage(e.target.value),
                    })
                  }
                >
                  {INTEGRAL_COVERAGE_OPTIONS.map((option) => (
                    <option
                      key={integralCoverageValue(option.amount)}
                      value={integralCoverageValue(option.amount)}
                    >
                      {option.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
            {rows.length > 1 ? (
              <Field.Root>
                <Field.Label visibility="hidden">Quitar</Field.Label>
                <Button
                  variant="ghost"
                  aria-label="Quitar fianza o seguro"
                  onClick={() => onRemove(row.id)}
                >
                  Quitar
                </Button>
              </Field.Root>
            ) : (
              <span />
            )}
          </Box>
        );
      })}
      <Box display="flex" justifyContent="space-between" gap="3">
        <Button variant="outline" onClick={onAdd}>
          Agregar otra fianza o seguro
        </Button>
        <Button disabled={!isBenchValid(rows, portfolio)} onClick={onNext}>
          Calcular mi ahorro
        </Button>
      </Box>
    </Card.Body>
  </Card.Root>
);

export default BenchStep;
